import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import { DEALERSHIP_INFO } from '../config/dealership';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#000000] pt-20" data-testid="terms-page">
      {/* Header */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#141414] via-[#0A0A0A] to-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-4"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#CCAD60]/20">
              <FileText size={24} className="text-[#CCAD60]" />
            </div>
            <div>
              <h1 
                className="font-serif text-3xl md:text-4xl font-bold text-[white] tracking-tight"
                data-testid="terms-title"
              >
                Terms of Service
              </h1>
              <p className="text-[zinc-500] font-sans text-sm">Last updated: January 2025</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <div className="space-y-8 text-[zinc-500] font-sans">
              {/* Introduction */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">About This Website</h2>
                <p className="leading-relaxed">
                  This website is operated by {DEALERSHIP_INFO.name}. By using this website, you agree to these terms. 
                  This is an informational website for viewing vehicle listings and submitting enquiries.
                </p>
              </div>

              {/* Enquiries */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">Enquiries & Contact Forms</h2>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Submitting an enquiry form does not guarantee the purchase, sale, or service of a vehicle</li>
                  <li>All enquiries are subject to availability and our team will contact you to discuss further</li>
                  <li>By submitting a form, you consent to being contacted by {DEALERSHIP_INFO.name} regarding your enquiry</li>
                  <li>We aim to respond to all enquiries promptly but cannot guarantee specific response times</li>
                </ul>
              </div>

              {/* Finance Calculator */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">Finance Calculator</h2>
                <p className="leading-relaxed mb-4">
                  The finance calculator on this website provides <span className="text-[white] font-medium">estimates only</span>. 
                  These figures are for illustration purposes and do not constitute a formal finance offer.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Final monthly payments, interest rates, and terms are subject to approval</li>
                  <li>Finance is subject to status and credit checks</li>
                  <li>Actual rates may differ from displayed estimates</li>
                  <li>Finance is arranged through third-party providers</li>
                </ul>
              </div>

              {/* Vehicle Listings */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">Vehicle Listings</h2>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Vehicle listings are for informational purposes only</li>
                  <li>Prices, specifications, and availability are subject to change without notice</li>
                  <li>We take reasonable care to ensure accuracy but cannot guarantee all information is error-free</li>
                  <li>Images shown may be representative and actual vehicles may vary</li>
                  <li>We are not liable for pricing errors or outdated listings</li>
                </ul>
              </div>

              {/* Sell Your Car */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">Selling Your Vehicle</h2>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Submitting a sell-your-car form is an expression of interest only</li>
                  <li>All vehicle valuations are subject to inspection</li>
                  <li>We are under no obligation to purchase any vehicle submitted</li>
                  <li>Final purchase offers are made at our discretion following vehicle assessment</li>
                </ul>
              </div>

              {/* Repairs & Services */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">Repairs & Services</h2>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Repair enquiries are requests for information or booking, not confirmed appointments</li>
                  <li>Final costs are confirmed following vehicle inspection</li>
                  <li>We will contact you to confirm appointment times and provide quotes</li>
                </ul>
              </div>

              {/* Limitation of Liability */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">Limitation of Liability</h2>
                <p className="leading-relaxed">
                  While we strive to provide accurate information, {DEALERSHIP_INFO.name} is not liable for any errors, 
                  omissions, or outdated information on this website. Use of this website is at your own risk.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[white] mb-4">Contact Us</h2>
                <p className="leading-relaxed mb-4">
                  If you have any questions about these terms, please contact us:
                </p>
                <div className="space-y-2">
                  <p><span className="text-[white]">Business:</span> {DEALERSHIP_INFO.name}</p>
                  <p><span className="text-[white]">Email:</span> <a href={DEALERSHIP_INFO.emailHref} className="text-[#CCAD60] hover:underline">{DEALERSHIP_INFO.email}</a></p>
                  <p><span className="text-[white]">Phone:</span> <a href={DEALERSHIP_INFO.phoneHref} className="text-[#CCAD60] hover:underline">{DEALERSHIP_INFO.phone}</a></p>
                  <p><span className="text-[white]">Address:</span> {DEALERSHIP_INFO.address.full}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
